/**
 * EditableDescriptionCell - inline-editable description for the bank feed table.
 *
 * Behaviour:
 *  • Idle → shows description text with a tiny pencil + a "Type a clearer
 *    description, AI re-suggests the GL" affordance icon. Click pencil OR
 *    double-click text to enter edit mode.
 *  • Editing → autosize textarea; Esc cancels, Enter (without shift) saves.
 *  • Saving → spinner; on success the new text becomes the canonical value.
 *  • After save → if `onRequestAiSuggestion` is provided, we call it with
 *    the new description. If the response is a non-empty GL different
 *    from the row's current GL, we surface a small pill
 *       "✨ AI suggests: <GL name>  [Apply]"
 *    that the user can apply with one click.
 *
 * The bank-statement reference is preserved separately and is unaffected
 * by edits here.
 */
import React, { useState, useRef, useEffect } from 'react';
import { Pencil, Loader2, Check, X, Sparkles, Brain } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';

export const EditableDescriptionCell = ({
  value,
  reference,
  onSave,                  // (newDescription) => Promise<void>
  onRequestAiSuggestion,   // OPTIONAL: (newDescription) => Promise<{ suggested_gl_code, suggested_gl_name, is_different_from_current, confidence } | null>
  onApplyAiSuggestion,     // OPTIONAL: (suggestion) => Promise<void>
  disabled = false,
  testId,
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const [aiApplying, setAiApplying] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { if (!editing) setDraft(value || ''); }, [value, editing]);
  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const handleSave = async () => {
    const next = (draft || '').trim();
    if (!next || next === (value || '').trim()) { setEditing(false); return; }
    setSaving(true);
    try {
      await onSave?.(next);
      setEditing(false);
      // Fire AI re-suggestion (best effort - never blocks the save).
      if (onRequestAiSuggestion) {
        setAiLoading(true);
        try {
          const suggestion = await onRequestAiSuggestion(next);
          if (suggestion && suggestion.is_different_from_current && suggestion.suggested_gl_code) {
            setAiSuggestion(suggestion);
          } else {
            setAiSuggestion(null);
          }
        } catch {
          // silent - suggestion is a nice-to-have
        } finally {
          setAiLoading(false);
        }
      }
    } catch {
      // Toast handled upstream
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => { setDraft(value || ''); setEditing(false); };

  const handleApply = async () => {
    if (!aiSuggestion || !onApplyAiSuggestion) return;
    setAiApplying(true);
    try {
      await onApplyAiSuggestion(aiSuggestion);
      setAiSuggestion(null);
    } catch {
      // toast upstream
    } finally {
      setAiApplying(false);
    }
  };

  if (editing) {
    return (
      <div className="flex items-start gap-1" data-testid={testId ? `${testId}-editor` : undefined}>
        <textarea
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') { e.preventDefault(); handleCancel(); }
            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSave(); }
          }}
          rows={2}
          maxLength={500}
          className="flex-1 min-w-[120px] resize-none text-xs rounded border border-violet-400/60 px-1.5 py-1 focus:outline-none focus:ring-2 focus:ring-violet-400/30"
          disabled={saving}
          data-testid={testId ? `${testId}-input` : undefined}
          placeholder="e.g. Domiciliary fee. KQ flight Lagos→Nairobi"
        />
        <div className="flex flex-col gap-0.5 mt-0.5">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="h-5 w-5 rounded text-emerald-600 hover:bg-emerald-50 grid place-items-center"
            data-testid={testId ? `${testId}-save` : undefined}
            aria-label="Save description"
          >
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
          </button>
          <button
            type="button"
            onClick={handleCancel}
            disabled={saving}
            className="h-5 w-5 rounded text-rose-600 hover:bg-rose-50 grid place-items-center"
            data-testid={testId ? `${testId}-cancel` : undefined}
            aria-label="Cancel"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1">
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={() => !disabled && setEditing(true)}
              onDoubleClick={() => !disabled && setEditing(true)}
              disabled={disabled}
              className="group/desc w-full text-left flex items-start gap-1 rounded px-1 -mx-1 py-0.5
                border-b border-dashed border-slate-300 hover:border-violet-400 hover:bg-violet-50/60
                focus:outline-none focus:ring-1 focus:ring-violet-400/40 transition-colors"
              data-testid={testId}
            >
              <span className="text-xs text-muted-foreground line-clamp-2 flex-1 break-words">
                {value || reference || '-'}
              </span>
              <Brain className="w-3 h-3 text-violet-500/70 opacity-0 group-hover/desc:opacity-100 shrink-0 mt-0.5" />
              <Pencil className="w-3 h-3 text-slate-400 group-hover/desc:text-violet-500 shrink-0 mt-0.5" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-[320px] text-xs">
            <div className="space-y-1">
              <p className="whitespace-pre-wrap">{value || reference || 'No description'}</p>
              {reference && reference !== value && (
                <p className="text-[10px] opacity-70">Bank reference: {reference}</p>
              )}
              <div className="flex items-center gap-1 text-[10px] italic text-violet-600">
                <Sparkles className="w-2.5 h-2.5" />
                Type a clearer description → AI re-suggests the right GL
              </div>
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      {aiLoading && (
        <div className="flex items-center gap-1 text-[10px] text-violet-600" data-testid={testId ? `${testId}-ai-loading` : undefined}>
          <Loader2 className="w-2.5 h-2.5 animate-spin" />
          AI is re-suggesting…
        </div>
      )}

      {aiSuggestion && !aiLoading && (
        <div
          className="flex items-center gap-1.5 px-1.5 py-1 rounded bg-violet-50 border border-violet-200 text-[10.5px]"
          data-testid={testId ? `${testId}-ai-suggestion` : undefined}
        >
          <Sparkles className="w-3 h-3 text-violet-600 shrink-0" />
          <span className="text-violet-800">
            AI suggests: <b>{aiSuggestion.suggested_gl_name}</b>
            {aiSuggestion.confidence > 0 && (
              <span className="text-violet-500 ml-1">· {Math.round((aiSuggestion.confidence || 0) * 100)}%</span>
            )}
          </span>
          <button
            type="button"
            onClick={handleApply}
            disabled={aiApplying || !onApplyAiSuggestion}
            className="ml-auto h-5 px-1.5 rounded bg-violet-600 hover:bg-violet-700 text-white font-medium text-[10px] disabled:opacity-60"
            data-testid={testId ? `${testId}-ai-apply` : undefined}
          >
            {aiApplying ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Apply'}
          </button>
          <button
            type="button"
            onClick={() => setAiSuggestion(null)}
            className="h-5 w-5 rounded text-violet-700 hover:bg-violet-100 grid place-items-center"
            data-testid={testId ? `${testId}-ai-dismiss` : undefined}
            aria-label="Dismiss suggestion"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      )}
    </div>
  );
};

export default EditableDescriptionCell;
